import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import { Link } from 'react-router-dom';

function getPreferredName(currentUser) {
  const firstName = currentUser?.firstName?.trim();
  if (firstName) {
    return firstName;
  }

  const displayName = currentUser?.displayName?.trim();
  if (displayName) {
    return displayName.split(/\s+/)[0];
  }

  return currentUser?.username || 'Account';
}

export default function AccountMenu({
  currentUser,
  onLogout,
  buttonSx,
  showCacheStatusLink = false,
  cacheStatusDisabled = false,
  showTenantSettingsLink = false,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const isOpen = Boolean(anchorEl);
  const preferredName = getPreferredName(currentUser);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    if (!isLoggingOut) {
      setAnchorEl(null);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    setAnchorEl(null);

    try {
      await onLogout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
        endIcon={<KeyboardArrowDownRoundedIcon />}
        sx={{
          borderRadius: '999px',
          px: 1.1,
          py: 0.75,
          minWidth: 0,
          textTransform: 'none',
          color: 'text.primary',
          border: '1px solid rgba(0,0,0,0.08)',
          backgroundColor: 'rgba(255,255,255,0.9)',
          '&:hover': {
            backgroundColor: '#fff',
            borderColor: 'rgba(0,0,0,0.12)',
          },
          ...buttonSx,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'rgba(15,118,110,0.14)', color: '#0f766e' }}>
            {preferredName.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0, display: { xs: 'none', sm: 'block' }, textAlign: 'left' }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {preferredName}
            </Typography>
          </Box>
        </Stack>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {showTenantSettingsLink && (
          <MenuItem component={Link} to="/tenant-settings" onClick={handleClose}>
            <ListItemIcon>
              <SettingsSuggestIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Tenant settings</ListItemText>
          </MenuItem>
        )}
        {showCacheStatusLink && (
          <MenuItem
            component={cacheStatusDisabled ? 'button' : Link}
            to={cacheStatusDisabled ? undefined : '/cache-status'}
            onClick={cacheStatusDisabled ? undefined : handleClose}
            disabled={cacheStatusDisabled}
          >
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Cache status</ListItemText>
          </MenuItem>
        )}
        <MenuItem component={Link} to="/profile" onClick={handleClose}>
          <ListItemIcon>
            <PersonOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogout} disabled={isLoggingOut}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{isLoggingOut ? 'Logging out...' : 'Logout'}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
